(() => {
  const API_KEY = "__biceek3dStoryMedia";

  if (window[API_KEY]) {
    window[API_KEY].refresh();
    return;
  }

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const controllers = new Map();


  const setPlayingState = (media, playing) => {
    media.classList.toggle("is-playing", playing);
  };

  const postProviderCommand = (iframe, command) => {
    if (!iframe?.contentWindow) return;

    const payload =
      iframe.dataset.bfProvider === "youtube"
        ? {
            event: "command",
            func: command === "play" ? "playVideo" : "pauseVideo",
            args: [],
          }
        : { method: command };

    iframe.contentWindow.postMessage(JSON.stringify(payload), "*");
  };

  const pauseAllExcept = (activeController = null) => {
    controllers.forEach((controller) => {
      if (controller !== activeController) controller.pause();
    });
  };

  class ProductStoryController {
    constructor(root) {
      this.root = root;
      this.media = root.querySelector("[data-bf-story-media]");
      this.video = root.querySelector("[data-bf-story-video]");
      this.iframe = root.querySelector("[data-bf-story-iframe]");
      this.abortController = new AbortController();
      this.preloadObserver = null;
      this.playbackObserver = null;
      this.userInitiatedPlayback = false;
      this.originalLoop = this.video?.loop || false;
      this.mount();
    }

    mount() {
      const { signal } = this.abortController;


      this.video?.addEventListener("play", () => this.handlePlaying(), {
        signal,
      });
      this.video?.addEventListener("pause", () => this.handlePaused(), {
        signal,
      });
      this.video?.addEventListener("ended", () => this.handlePaused(), {
        signal,
      });
      this.video?.addEventListener("loadeddata", () => this.markReady(), {
        signal,
      });
      this.video?.addEventListener("error", () => this.handleMediaError(), {
        signal,
      });
      this.iframe?.addEventListener("load", () => this.markReady(), { signal });
      this.iframe?.addEventListener("error", () => this.handleMediaError(), {
        signal,
      });

      if (reducedMotion.matches) {
        this.unloadDynamicMedia();
        return;
      }

      if (!("IntersectionObserver" in window)) {
        this.hydrateNativeVideo();
        if (this.media.dataset.bfAutoplay === "true") this.play();
        return;
      }

      if (this.video) {
        this.preloadObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.hydrateNativeVideo();
            this.preloadObserver?.disconnect();
          },
          { rootMargin: "400px 0px", threshold: 0.01 },
        );
        this.preloadObserver.observe(this.media);
      }

      this.playbackObserver = new IntersectionObserver(
        (entries) => this.handleVisibility(entries[0]),
        { threshold: [0, 0.25, 0.55, 0.75] },
      );
      this.playbackObserver.observe(this.media);
    }

    handleVisibility(entry) {
      if (!entry?.isIntersecting || entry.intersectionRatio < 0.25) {
        this.pause();
        return;
      }

      const shouldAutoplay =
        this.media.dataset.bfAutoplay === "true" &&
        this.media.dataset.bfScrollTrigger === "true";

      if (shouldAutoplay && entry.intersectionRatio >= 0.55) this.play();
    }

    hydrateNativeVideo({ userInitiated = false } = {}) {
      if (!this.video || (reducedMotion.matches && !userInitiated)) return;
      if (this.video.dataset.bfHydrated === "true") return;

      if (reducedMotion.matches && userInitiated) this.video.loop = false;

      this.video.querySelectorAll("source[data-src]").forEach((source) => {
        source.src = source.dataset.src;
      });
      this.video.dataset.bfHydrated = "true";
      this.video.load();
    }

    hydrateExternalVideo(autoplay, { userInitiated = false } = {}) {
      if (!this.iframe || (reducedMotion.matches && !userInitiated)) return null;

      if (!this.iframe.src) {
        let source = this.iframe.dataset.src;
        if (reducedMotion.matches && userInitiated) {
          try {
            const sourceUrl = new URL(source);
            sourceUrl.searchParams.delete("loop");
            sourceUrl.searchParams.delete("playlist");
            source = sourceUrl.toString();
          } catch (_error) {
            // Keep the validated provider URL if it cannot be normalized.
          }
        }

        const separator = source.includes("?") ? "&" : "?";
        this.iframe.src = `${source}${
          autoplay ? `${separator}autoplay=1` : ""
        }`;
      }

      return this.iframe;
    }

    unloadDynamicMedia() {
      this.pause();
      this.userInitiatedPlayback = false;
      this.media.classList.remove("is-media-ready");

      if (this.video?.dataset.bfHydrated === "true") {
        this.video.querySelectorAll("source[data-src]").forEach((source) =>
          source.removeAttribute("src"),
        );
        delete this.video.dataset.bfHydrated;
        this.video.loop = this.originalLoop;
        this.video.load();
      }

      this.iframe?.removeAttribute("src");
    }

    markReady() {
      if (this.video?.readyState >= 2 && !this.video.error) this.media.classList.remove("is-media-error");
      if (
        (!reducedMotion.matches || this.userInitiatedPlayback) &&
        !this.media.classList.contains("is-media-error")
      ) {
        this.media.classList.add("is-media-ready");
      }
    }

    play({ userInitiated = false } = {}) {
      if (document.hidden || (reducedMotion.matches && !userInitiated)) return;
      if (this.media.classList.contains("is-media-error")) {
        if (!userInitiated) return;
        this.media.classList.remove("is-media-error");
        if (this.video) delete this.video.dataset.bfHydrated;
      }

      this.userInitiatedPlayback = userInitiated;
      pauseAllExcept(this);

      if (this.video) {
        this.video.muted = true;
        this.video.controls = false;
        this.hydrateNativeVideo({ userInitiated });
        const playPromise = this.video.play();
        playPromise?.catch(() => setPlayingState(this.media, false));
        return;
      }

      const iframe = this.hydrateExternalVideo(true, { userInitiated });
      if (!iframe) return;

      postProviderCommand(iframe, "play");
      setPlayingState(this.media, true);
    }

    pause() {
      this.video?.pause();
      if (this.iframe?.src) postProviderCommand(this.iframe, "pause");
      setPlayingState(this.media, false);
    }

    handlePlaying() {
      pauseAllExcept(this);
      setPlayingState(this.media, true);
    }

    handlePaused() {
      setPlayingState(this.media, false);
    }

    handleMediaError() {
      this.pause();
      this.media.classList.remove("is-media-ready");
      this.media.classList.add("is-media-error");
    }

    handleProviderState(playing) {
      if (playing) pauseAllExcept(this);
      setPlayingState(this.media, playing);
    }

    destroy() {
      this.preloadObserver?.disconnect();
      this.playbackObserver?.disconnect();
      this.pause();
      this.abortController.abort();
    }
  }

  function refresh() {
    const roots = new Set(
      Array.from(document.querySelectorAll("[data-bf-3d-story]")).filter(
        (root) =>
          root.querySelector("[data-bf-story-video], [data-bf-story-iframe]"),
      ),
    );

    controllers.forEach((controller, root) => {
      if (!roots.has(root)) {
        controller.destroy();
        controllers.delete(root);
      }
    });

    roots.forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.set(root, new ProductStoryController(root));
    });
  }

  function removeWithin(scope) {
    controllers.forEach((controller, root) => {
      if (scope.contains(root) || root === scope) {
        controller.destroy();
        controllers.delete(root);
      }
    });
  }

  function handleProviderMessage(event) {
    if (!/youtube(?:-nocookie)?\.com$|vimeo\.com$/.test(event.origin)) return;

    let data = event.data;
    if (typeof data === "string") {
      try {
        data = JSON.parse(data);
      } catch (_error) {
        return;
      }
    }

    const controller = Array.from(controllers.values()).find(
      (candidate) => candidate.iframe?.contentWindow === event.source,
    );
    if (!controller) return;

    if (data.event === "play" || data.info?.playerState === 1) {
      controller.handleProviderState(true);
    } else if (
      data.event === "pause" ||
      data.event === "ended" ||
      data.info?.playerState === 2
    ) {
      controller.handleProviderState(false);
    }
  }

  const refreshForMotionPreference = () => {
    pauseAllExcept();
    refresh();
  };

  const handleVisibilityChange = () => {
    if (document.hidden) pauseAllExcept();
    else refresh();
  };

  reducedMotion.addEventListener("change", refreshForMotionPreference);
  document.addEventListener("shopify:section:load", refresh);
  document.addEventListener("shopify:section:unload", (event) =>
    removeWithin(event.target),
  );
  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("message", handleProviderMessage);

  window[API_KEY] = { refresh, pauseAllExcept };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refresh, { once: true });
  } else {
    refresh();
  }
})();
